import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import theme from '../../../UI/theme';
import Modal from '../../UiComponents/Modal';
import Button from '../../UiComponents/Button';
import InputField from '../../UiComponents/InputField';
import { ModalContainer, ButtonRowContainer, ModalText } from '../../panel/ModalStyles';
import { getTicketLabels, getTicketPipelines, getTicketStages } from '../../../scripts/hubspot';

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 2px solid ${theme.colors.grayBorder};
`;

const SectionHeading = styled.h3`
    margin: 0;
`;

const ConditionSetsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ConditionSetCard = styled.div`
    background: ${theme.colors.darkSpace80};
    border: 1px solid ${theme.colors.grayBorder};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ConditionSetHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;

const ConditionList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const ConditionChipWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const ConditionChip = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    border-radius: 999px;
    background: ${theme.colors.darkSpace70};
    border: 1px solid ${theme.colors.grayBorder};
    font-size: ${theme.fontSize.superTiny};
    color: ${theme.colors.whiteSpace};
`;

const ConditionConnector = styled.span`
    font-size: ${theme.fontSize.superTiny};
    font-weight: 600;
    color: ${theme.colors.textGray};
`;

const SlackBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: ${theme.fontSize.superTiny};
    font-weight: 600;
    background: ${({ $enabled }) => ($enabled ? theme.colors.greenEnergy : theme.colors.raspberryRed)};
    color: ${({ $enabled }) => ($enabled ? theme.colors.darkSpace90 : theme.colors.whiteSpace)};
    text-transform: uppercase;
    letter-spacing: 0.4px;
`;

const HubSpotBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: ${theme.fontSize.superTiny};
    font-weight: 600;
    background: #FF7A59;
    color: ${theme.colors.whiteSpace};
    text-transform: uppercase;
    letter-spacing: 0.4px;
`;

const LogicalConnector = styled.div`
    display: flex;
    justify-content: center;
    font-size: ${theme.fontSize.small};
    font-weight: 600;
    color: ${theme.colors.greenEnergy};
`;

const EmptyState = styled.div`
    color: ${theme.colors.textGray};
    font-size: ${theme.fontSize.small};
`;

const Select = styled.select`
    background: ${theme.colors.darkSpace80};
    color: ${theme.colors.whiteSpace};
    border: 1px solid ${theme.colors.grayBorder};
    border-radius: 4px;
    padding: 6px 8px;
    font-size: ${theme.fontSize.small};
`;

const FormRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const InlineRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const DraftConditionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DraftConditionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ErrorMessage = styled.p`
    margin: 0;
    font-size: ${theme.fontSize.small};
    color: ${theme.colors.raspberryRed};
`;

const CheckboxRow = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${theme.fontSize.small};
    cursor: pointer;

    input {
        width: 16px;
        height: 16px;
        accent-color: ${theme.colors.greenEnergy};
    }
`;

const CONDITION_TYPES = [
    { value: 'rowCount', label: 'Number of rows returned' },
    { value: 'columnValue', label: 'Column value check' },
    { value: 'columnContains', label: 'Column contains text' },
    { value: 'timeSince', label: 'Minutes since timestamp' },
];

const COMPARISON_OPERATORS = [
    { value: '>', label: 'Greater than' },
    { value: '>=', label: 'Greater than or equal to' },
    { value: '<', label: 'Less than' },
    { value: '<=', label: 'Less than or equal to' },
    { value: '=', label: 'Equal to' },
    { value: '!=', label: 'Not equal to' },
];

const OPERATOR_LABEL_LOOKUP = COMPARISON_OPERATORS.reduce((accumulator, operator) => {
    accumulator[operator.value] = operator.label.toLowerCase();
    return accumulator;
}, {});

export const DEFAULT_SLACK_NOTIFICATION_CHANNEL = 'WAVED_ERROR';

export const SLACK_NOTIFICATION_CHANNELS = [
    { value: 'WAVED_ERROR', label: 'Waved Error'},
    { value: 'CUSTOMER_SUCCESS', label: 'Customer Success' },
    { value: 'WAVED_MONITOR', label: 'Waved Monitor' }
];

const SLACK_NOTIFICATION_LABEL_LOOKUP = SLACK_NOTIFICATION_CHANNELS.reduce((accumulator, channel) => {
    accumulator[channel.value] = channel.label;
    return accumulator;
}, {});

export const normalizeNotificationChannel = (channel) => {
    const normalized = typeof channel === 'string' ? channel.trim().toUpperCase() : '';
    const isValid = SLACK_NOTIFICATION_CHANNELS.some(({ value }) => value === normalized);
    return isValid ? normalized : DEFAULT_SLACK_NOTIFICATION_CHANNEL;
};

const getNotificationChannelLabel = (channel) => {
    const normalized = normalizeNotificationChannel(channel);
    return SLACK_NOTIFICATION_LABEL_LOOKUP[normalized] || normalized;
};

const deriveNotificationLock = (conditions = []) => {
    const firstCondition = conditions.find((condition) => condition && typeof condition === 'object');

    if (!firstCondition) {
        return null;
    }

    const slackEnabled = isSlackEnabled(firstCondition.slackNotification);

    if (!slackEnabled) {
        return { type: 'MARK', channel: null };
    }

    return {
        type: 'SLACK',
        channel: normalizeNotificationChannel(firstCondition.notificationChannel),
    };
};

const deriveHubspotLock = (conditions = []) => {
    const firstCondition = conditions.find((condition) => condition && typeof condition === 'object');
    if (!firstCondition) {
        return null;
    }
    return firstCondition.createHubspotTicket === true;
};

const enforceNotificationConsistency = (conditions = []) => {
    const lock = deriveNotificationLock(conditions);
    const hubspotLock = deriveHubspotLock(conditions);

    if (!lock && hubspotLock === null) {
        return conditions;
    }

    let hasAdjustments = false;
    let result = conditions;

    if (lock) {
        const alignedConditions = conditions.map((condition) => {
            const slackEnabled = isSlackEnabled(condition?.slackNotification);

            if (lock.type === 'MARK') {
                if (!slackEnabled && condition?.slackNotification === false && condition?.createHubspotTicket !== true) {
                    return condition;
                }

                hasAdjustments = true;
                return {
                    ...condition,
                    slackNotification: false,
                    createHubspotTicket: false,
                    ticket: { heading: '', pipeline: null, stage: null, label: null },
                };
            }

            const currentChannel = normalizeNotificationChannel(condition?.notificationChannel);

            if (slackEnabled && currentChannel === lock.channel) {
                return condition;
            }

            hasAdjustments = true;
            return {
                ...condition,
                slackNotification: true,
                notificationChannel: lock.channel,
            };
        });

        result = hasAdjustments ? alignedConditions : conditions;
    }

    if (hubspotLock !== null) {
        let hubspotAdjusted = false;
        const afterHubspot = result.map((condition) => {
            if ((condition?.createHubspotTicket === true) !== hubspotLock) {
                hubspotAdjusted = true;
                return { ...condition, createHubspotTicket: hubspotLock };
            }
            return condition;
        });

        if (hubspotAdjusted) {
            return afterHubspot;
        }
    }

    return result;
};

const defaultDraftCondition = {
    kind: 'rowCount',
    operator: '>',
    value: '',
    column: '',
    slackNotification: true,
    notificationChannel: DEFAULT_SLACK_NOTIFICATION_CHANNEL,
    createHubspotTicket: false,
    ticket: { heading: '', pipeline: null, stage: null, label: null },
};

const createDefaultDraftCondition = () => ({ ...defaultDraftCondition });

const isSlackEnabled = (value) => {
    if (value === true || value === 1) {
        return true;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return normalized === 'true' || normalized === '1';
    }

    return false;
};

const operatorForKind = (kind) => {
    if (kind === 'rowCount') {
        return '>';
    }

    if (kind === 'columnValue') {
        return '=';
    }

    return undefined;
};

const normalizeCondition = (condition) => {
    if (!condition || typeof condition !== 'object') {
        return null;
    }

    const kind = condition.kind || condition.type;
    const operator = condition.operator || condition.comparison;
    const slackNotification = isSlackEnabled(condition.slackNotification);
    const notificationChannel = normalizeNotificationChannel(condition.notificationChannel);
    const createHubspotTicket = condition.createHubspotTicket === true;
    const heading = createHubspotTicket && typeof condition.ticket?.heading === 'string'
        ? condition.ticket.heading.trim()
        : '';
    const pipeline = createHubspotTicket && condition.ticket?.pipeline?.idValue
        ? { label: condition.ticket.pipeline.label || '', idValue: condition.ticket.pipeline.idValue }
        : null;
    const stage = createHubspotTicket && condition.ticket?.stage?.idValue
        ? { label: condition.ticket.stage.label || '', idValue: condition.ticket.stage.idValue }
        : null;
    const label = createHubspotTicket && condition.ticket?.label?.idValue
        ? { label: condition.ticket.label.label || '', idValue: condition.ticket.label.idValue }
        : null;

    if (kind === 'rowCount' || kind === 'nRows') {
        const numericValue = Number(condition.value);
        if (!Number.isFinite(numericValue)) {
            return null;
        }

        return {
            kind: 'rowCount',
            operator: operator || '>',
            value: numericValue,
            slackNotification,
            notificationChannel,
            createHubspotTicket,
            ticket: { heading, pipeline, stage, label },
        };
    }

    if (kind === 'columnValue' || kind === 'columnCheck') {
        const column = condition.column || condition.columnName || condition.field || '';
        const value = condition.value ?? condition.expectedValue ?? '';

        const normalizedColumn = String(column).trim();

        if (!normalizedColumn) {
            return null;
        }

        if (value === undefined || value === null || value === '') {
            return null;
        }

        return {
            kind: 'columnValue',
            operator: operator || '=',
            column: normalizedColumn,
            value,
            slackNotification,
            notificationChannel,
            createHubspotTicket,
            ticket: { heading, pipeline, stage, label },
        };
    }

    if (
        kind === 'columnContains' ||
        kind === 'contains' ||
        kind === 'columnIncludes'
    ) {
        const column = condition.column || condition.columnName || condition.field || '';
        const value = condition.value ?? condition.search ?? condition.expectedValue ?? '';

        const normalizedColumn = String(column).trim();

        if (!normalizedColumn) {
            return null;
        }

        if (value === undefined || value === null || value === '') {
            return null;
        }

        const normalizedValue = String(value);

        if (normalizedValue.trim() === '') {
            return null;
        }

        return {
            kind: 'columnContains',
            column: normalizedColumn,
            value: normalizedValue,
            slackNotification,
            notificationChannel,
            createHubspotTicket,
            ticket: { heading, pipeline, stage, label },
        };
    }

    if (kind === 'timeSince') {
        const column = condition.column || condition.columnName || condition.field || '';
        const numericValue = Number(condition.value);

        const normalizedColumn = String(column).trim();

        if (!normalizedColumn || !Number.isFinite(numericValue)) {
            return null;
        }

        return {
            kind: 'timeSince',
            column: normalizedColumn,
            value: numericValue,
            slackNotification,
            notificationChannel,
            createHubspotTicket,
            ticket: { heading, pipeline, stage, label },
        };
    }

    return null;
};

const formatCondition = (condition) => {
    if (!condition) {
        return '';
    }

    const operatorLabel = OPERATOR_LABEL_LOOKUP[condition.operator] || condition.operator;

    if (condition.kind === 'rowCount') {
        return `Row count ${operatorLabel} ${condition.value}`;
    }

    if (condition.kind === 'columnValue') {
        return `${condition.column} ${operatorLabel} ${condition.value}`;
    }

    if (condition.kind === 'columnContains') {
        return `${condition.column} contains "${condition.value}"`;
    }

    if (condition.kind === 'timeSince') {
        return `${condition.column} is older than ${condition.value} minute${condition.value === 1 ? '' : 's'}`;
    }

    return 'Unsupported condition';
};

const CustomSqlNotificationEditor = ({ value = [], onChange, columnSuggestions = [] }) => {
    const normalizedGroups = useMemo(() => {
        if (!Array.isArray(value)) {
            return [];
        }

        return value
            .map((group) => {
                if (!Array.isArray(group)) {
                    return [];
                }

                const normalizedGroup = group
                    .map((condition) => normalizeCondition(condition))
                    .filter(Boolean);

                return enforceNotificationConsistency(normalizedGroup);
            })
            .filter((group) => group.length > 0);
    }, [value]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [draftConditions, setDraftConditions] = useState([]);
    const [draftCondition, setDraftCondition] = useState(createDefaultDraftCondition());
    const [draftError, setDraftError] = useState('');
    const [ticketLabels, setTicketLabels] = useState([]);
    const [ticketPipelines, setTicketPipelines] = useState([]);
    const [ticketStages, setTicketStages] = useState([]);

    useEffect(() => {
        getTicketLabels()
            .then((data) => setTicketLabels(Array.isArray(data) ? data : []))
            .catch(() => setTicketLabels([]));
        getTicketPipelines()
            .then((data) => setTicketPipelines(Array.isArray(data) ? data : []))
            .catch(() => setTicketPipelines([]));
        getTicketStages()
            .then((data) => setTicketStages(Array.isArray(data) ? data : []))
            .catch(() => setTicketStages([]));
    }, []);

    const lockedNotificationState = useMemo(
        () => deriveNotificationLock(draftConditions),
        [draftConditions]
    );
    const lockedNotificationType = lockedNotificationState?.type || null;
    const lockedNotificationChannel = lockedNotificationState?.channel || null;
    const lockedHubspotTicket = useMemo(
        () => deriveHubspotLock(draftConditions),
        [draftConditions]
    );
    const allowedNotificationChannels =
        lockedNotificationType === 'SLACK' && lockedNotificationChannel
            ? SLACK_NOTIFICATION_CHANNELS.filter((channel) => channel.value === lockedNotificationChannel)
            : SLACK_NOTIFICATION_CHANNELS;

    const uniqueColumnSuggestions = useMemo(() => {
        if (!Array.isArray(columnSuggestions)) {
            return [];
        }

        return Array.from(
            new Set(
                columnSuggestions
                    .filter((item) => typeof item === 'string')
                    .map((item) => item.trim())
                    .filter(Boolean)
            )
        );
    }, [columnSuggestions]);


    useEffect(() => {
        if (!lockedNotificationType) {
            return;
        }

        setDraftCondition((previous) => {
            if (lockedNotificationType === 'MARK') {
                if (previous.slackNotification === true || previous.createHubspotTicket === true) {
                    return {
                        ...previous,
                        slackNotification: false,
                        createHubspotTicket: false,
                        ticket: { heading: '', pipeline: null, stage: null, label: null },
                    };
                }

                return previous;
            }

            const normalizedChannel = normalizeNotificationChannel(previous.notificationChannel);
            if (previous.slackNotification === true && normalizedChannel === lockedNotificationChannel) {
                return previous;
            }

            return {
                ...previous,
                slackNotification: true,
                notificationChannel: lockedNotificationChannel || DEFAULT_SLACK_NOTIFICATION_CHANNEL,
            };
        });
    }, [
        lockedNotificationType,
        lockedNotificationChannel,
        draftCondition.slackNotification,
        draftCondition.notificationChannel,
    ]);

    useEffect(() => {
        if (lockedHubspotTicket === null) {
            return;
        }

        setDraftCondition((previous) => {
            if ((previous.createHubspotTicket === true) === lockedHubspotTicket) {
                return previous;
            }
            return {
                ...previous,
                createHubspotTicket: lockedHubspotTicket,
            };
        });
    }, [lockedHubspotTicket, draftCondition.createHubspotTicket]);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingIndex(null);
    setDraftConditions([]);
    setDraftCondition(createDefaultDraftCondition());
        setDraftError('');
    };

    const openForNewSet = () => {
        setIsModalOpen(true);
        setEditingIndex(null);
    setDraftConditions([]);
    setDraftCondition(createDefaultDraftCondition());
        setDraftError('');
    };

    const openForEdit = (index) => {
        const targetGroup = normalizedGroups[index] || [];
        setIsModalOpen(true);
        setEditingIndex(index);
        const normalizedDraft = targetGroup.map((condition) => ({
            ...condition,
            slackNotification: isSlackEnabled(condition?.slackNotification),
            notificationChannel: normalizeNotificationChannel(condition?.notificationChannel),
            createHubspotTicket: condition?.createHubspotTicket === true,
            ticket: {
                heading: condition?.ticket?.heading || '',
                pipeline: condition?.ticket?.pipeline || null,
                stage: condition?.ticket?.stage || null,
                label: condition?.ticket?.label || null,
            },
        }));

        setDraftConditions(enforceNotificationConsistency(normalizedDraft));
    setDraftCondition(createDefaultDraftCondition());
        setDraftError('');
    };

    const emitChange = (nextGroups) => {
        if (typeof onChange === 'function') {
            onChange(nextGroups);
        }
    };

    const handleRemoveSet = (index) => {
        const nextGroups = normalizedGroups.filter((_, groupIndex) => groupIndex !== index);
        emitChange(nextGroups);
    };

    const handleRemoveDraftCondition = (index) => {
        setDraftConditions((previous) => {
            const next = previous.filter((_, conditionIndex) => conditionIndex !== index);
            return enforceNotificationConsistency(next);
        });
        setDraftError('');
    };

    const isDraftValid = useMemo(() => {
        if (draftCondition.kind === 'rowCount') {
            return draftCondition.value !== '' && Number.isFinite(Number(draftCondition.value));
        }

        if (draftCondition.kind === 'columnValue') {
            return Boolean(
                draftCondition.column &&
                    draftCondition.column.trim() !== '' &&
                    draftCondition.value !== '' &&
                    draftCondition.operator
            );
        }

        if (draftCondition.kind === 'columnContains') {
            return Boolean(
                draftCondition.column &&
                    draftCondition.column.trim() !== '' &&
                    draftCondition.value !== '' &&
                    String(draftCondition.value).trim() !== ''
            );
        }

        if (draftCondition.kind === 'timeSince') {
            return Boolean(
                draftCondition.column &&
                    draftCondition.column.trim() !== '' &&
                    draftCondition.value !== '' &&
                    Number.isFinite(Number(draftCondition.value))
            );
        }

        return false;
    }, [draftCondition]);

    const handleAddDraftCondition = () => {
        if (!isDraftValid) {
            setDraftError('Please complete the condition before adding it.');
            return;
        }

        const kind = draftCondition.kind;
        const baseValue =
            kind === 'rowCount' || kind === 'timeSince'
                ? Number(draftCondition.value)
                : draftCondition.value;
        const normalizedChannel = normalizeNotificationChannel(draftCondition.notificationChannel);

        if (lockedNotificationType === 'MARK' && draftCondition.slackNotification === true) {
            setDraftError('All notifications in this condition set are mark-only. Remove existing conditions to switch to Slack.');
            return;
        }

        if (lockedNotificationType === 'SLACK' && draftCondition.slackNotification !== true) {
            setDraftError('All notifications in this condition set send Slack alerts. Remove existing conditions to switch to mark-only.');
            return;
        }

        if (
            draftCondition.slackNotification === true &&
            lockedNotificationChannel &&
            normalizedChannel !== lockedNotificationChannel
        ) {
            setDraftError('All Slack notifications inside a condition set must share the same channel.');
            return;
        }

        if (lockedHubspotTicket !== null && (draftCondition.createHubspotTicket === true) !== lockedHubspotTicket) {
            setDraftError('All conditions in this set must have the same HubSpot ticket setting.');
            return;
        }

        const newCondition = {
            kind,
            ...(kind !== 'timeSince' && draftCondition.operator
                ? { operator: draftCondition.operator }
                : {}),
            value: baseValue,
            ...((
                kind === 'columnValue' ||
                kind === 'timeSince' ||
                kind === 'columnContains'
            ) && draftCondition.column
                ? { column: draftCondition.column.trim() }
                : {}),
            slackNotification: draftCondition.slackNotification === true,
            notificationChannel: normalizedChannel,
            createHubspotTicket: draftCondition.createHubspotTicket === true,
            ticket: draftCondition.createHubspotTicket === true
                ? {
                    heading: draftCondition.ticket?.heading || '',
                    pipeline: draftCondition.ticket?.pipeline || null,
                    stage: draftCondition.ticket?.stage || null,
                    label: draftCondition.ticket?.label || null,
                }
                : { heading: '', pipeline: null, stage: null, label: null },
        };

        const nextDraftConditions = enforceNotificationConsistency([...draftConditions, newCondition]);
        const nextLockedNotificationState = deriveNotificationLock(nextDraftConditions);

        setDraftConditions(nextDraftConditions);
        setDraftCondition((previous) => ({
            kind: previous.kind,
            operator: operatorForKind(previous.kind),
            value: '',
            column: '',
            ...(nextLockedNotificationState?.type === 'MARK'
                ? {
                      slackNotification: false,
                      notificationChannel: DEFAULT_SLACK_NOTIFICATION_CHANNEL,
                  }
                : nextLockedNotificationState?.type === 'SLACK'
                    ? {
                          slackNotification: true,
                          notificationChannel:
                              nextLockedNotificationState.channel || DEFAULT_SLACK_NOTIFICATION_CHANNEL,
                      }
                    : {
                          slackNotification: previous.slackNotification === true,
                          notificationChannel:
                              previous.notificationChannel || DEFAULT_SLACK_NOTIFICATION_CHANNEL,
                      }),
            createHubspotTicket: nextDraftConditions.length > 0
                ? nextDraftConditions[0]?.createHubspotTicket === true
                : previous.createHubspotTicket === true,
            ticket: nextDraftConditions.length > 0
                ? {
                    heading: nextDraftConditions[0]?.ticket?.heading || '',
                    pipeline: nextDraftConditions[0]?.ticket?.pipeline || null,
                    stage: nextDraftConditions[0]?.ticket?.stage || null,
                    label: nextDraftConditions[0]?.ticket?.label || null,
                }
                : {
                    heading: previous?.ticket?.heading || '',
                    pipeline: previous?.ticket?.pipeline || null,
                    stage: previous?.ticket?.stage || null,
                    label: previous?.ticket?.label || null,
                },
        }));
        setDraftError('');

        const cleanedDraft = nextDraftConditions
            .map((condition) => normalizeCondition(condition))
            .filter(Boolean);

        if (cleanedDraft.length === 0) {
            setDraftError('Add at least one valid condition before saving.');
            return;
        }

        if (editingIndex === null) {
            emitChange([...normalizedGroups, cleanedDraft]);
            setEditingIndex(normalizedGroups.length);
        } else {
            const nextGroups = normalizedGroups.map((group, index) =>
                index === editingIndex ? cleanedDraft : group
            );
            emitChange(nextGroups);
        }
    };

    return (
        <Section>
            <SectionHeading>Slack notifications</SectionHeading>
            <ModalText>
                Build one or more condition sets. All conditions inside a set must be true (logical AND).
                If any set matches, a notification is sent (logical OR between sets).
            </ModalText>

            {normalizedGroups.length === 0 ? (
                <EmptyState>No notification rules yet. Add a condition set to get started.</EmptyState>
            ) : (
                <ConditionSetsWrapper>
                    {normalizedGroups.map((group, groupIndex) => (
                        <React.Fragment key={`notify-group-${groupIndex}`}>
                            <ConditionSetCard>
                                <ConditionSetHeader>
                                    <ModalText style={{ margin: 0, fontWeight: 600 }}>
                                        Condition set {groupIndex + 1} – all must be met
                                    </ModalText>
                                    <InlineRow>
                                        <Button
                                            tertiary
                                            small
                                            type="button"
                                            onClick={() => openForEdit(groupIndex)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            secondary
                                            small
                                            type="button"
                                            onClick={() => handleRemoveSet(groupIndex)}
                                        >
                                            Remove
                                        </Button>
                                    </InlineRow>
                                </ConditionSetHeader>
                                <ConditionList>
                                    {group.map((condition, conditionIndex) => {
                                        const slackEnabled = isSlackEnabled(condition?.slackNotification);
                                        const channelLabel = getNotificationChannelLabel(condition?.notificationChannel);

                                        return (
                                            <React.Fragment key={`notify-cond-${groupIndex}-${conditionIndex}`}>
                                                <ConditionChipWrapper>
                                                    <ConditionChip>{formatCondition(condition)}</ConditionChip>
                                                    <SlackBadge $enabled={slackEnabled}>
                                                        {slackEnabled ? `Slack – ${channelLabel}` : 'Mark'}
                                                    </SlackBadge>
                                                    {condition?.createHubspotTicket === true && (
                                                        <HubSpotBadge>
                                                            {condition?.ticket?.heading ? `HubSpot – ${condition.ticket.heading}` : 'HubSpot'}
                                                        </HubSpotBadge>
                                                    )}
                                                </ConditionChipWrapper>
                                                {conditionIndex < group.length - 1 && (
                                                    <ConditionConnector>AND</ConditionConnector>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </ConditionList>
                            </ConditionSetCard>
                            {groupIndex < normalizedGroups.length - 1 && (
                                <LogicalConnector>OR</LogicalConnector>
                            )}
                        </React.Fragment>
                    ))}
                </ConditionSetsWrapper>
            )}

            <Button tertiary small type="button" onClick={openForNewSet}>
                Add condition set
            </Button>

            <Modal show={isModalOpen} close={handleCloseModal}>
                <ModalContainer style={{ minWidth: '360px', gap: '16px' }}>
                    <h3 style={{ margin: 0 }}>
                        {editingIndex === null ? 'Add notification rule' : 'Edit notification rule'}
                    </h3>
                    <ModalText>
                        Add one or more conditions to describe when Slack should be notified.
                        Conditions inside a set are combined with AND.
                    </ModalText>

                    {draftConditions.length > 0 && (
                        <DraftConditionsList>
                            {draftConditions.map((condition, index) => {
                                const slackEnabled = isSlackEnabled(condition?.slackNotification);
                                const channelLabel = getNotificationChannelLabel(condition?.notificationChannel);

                                return (
                                    <DraftConditionRow key={`draft-condition-${index}`}>
                                        <ConditionChipWrapper>
                                            <ConditionChip>{formatCondition(condition)}</ConditionChip>
                                            <SlackBadge $enabled={slackEnabled}>
                                                {slackEnabled ? `Slack – ${channelLabel}` : 'Mark'}
                                            </SlackBadge>
                                            {condition?.createHubspotTicket === true && (
                                                <HubSpotBadge>
                                                    {condition?.ticket?.heading ? `HubSpot – ${condition.ticket.heading}` : 'HubSpot'}
                                                </HubSpotBadge>
                                            )}
                                        </ConditionChipWrapper>
                                        <Button
                                            tertiary
                                            small
                                            type="button"
                                            onClick={() => handleRemoveDraftCondition(index)}
                                        >
                                            Remove
                                        </Button>
                                    </DraftConditionRow>
                                );
                            })}
                        </DraftConditionsList>
                    )}

                    <FormRow>
                        <label htmlFor="notify-condition-type">Condition type</label>
                        <Select
                            id="notify-condition-type"
                            value={draftCondition.kind}
                            onChange={(event) => {
                                const nextKind = event.target.value;
                                setDraftCondition((previous) => ({
                                    kind: nextKind,
                                    operator: operatorForKind(nextKind),
                                    value: '',
                                    column: '',
                                    slackNotification: previous.slackNotification === true,
                                    notificationChannel: previous.notificationChannel || DEFAULT_SLACK_NOTIFICATION_CHANNEL,
                                    createHubspotTicket: previous.createHubspotTicket === true,
                                    ticket: { ...previous?.ticket, heading: previous?.ticket?.heading || '' },
                                }));
                            }}
                        >
                            {CONDITION_TYPES.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                    </FormRow>

                    {(draftCondition.kind === 'columnValue' ||
                        draftCondition.kind === 'timeSince' ||
                        draftCondition.kind === 'columnContains') && (
                        <FormRow>
                            <label htmlFor="notify-condition-column">Column name</label>
                            <InputField
                                id="notify-condition-column"
                                placeholder="e.g. status"
                                value={draftCondition.column}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    setDraftCondition((previous) => ({
                                        ...previous,
                                        column: value,
                                    }));
                                }}
                            />
                            {uniqueColumnSuggestions.length > 0 && (
                                <ModalText style={{ fontSize: theme.fontSize.superTiny }}>
                                    Suggestions: {uniqueColumnSuggestions.join(', ')}
                                </ModalText>
                            )}
                        </FormRow>
                    )}

                    {(draftCondition.kind === 'rowCount' || draftCondition.kind === 'columnValue') && (
                        <FormRow>
                            <label htmlFor="notify-condition-operator">Operator</label>
                            <Select
                                id="notify-condition-operator"
                                value={draftCondition.operator || ''}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    setDraftCondition((previous) => ({
                                        ...previous,
                                        operator: value,
                                    }));
                                }}
                            >
                                {COMPARISON_OPERATORS.map((operator) => (
                                    <option key={operator.value} value={operator.value}>
                                        {operator.label}
                                    </option>
                                ))}
                            </Select>
                        </FormRow>
                    )}

                    <FormRow>
                        <label htmlFor="notify-condition-value">Value</label>
                        <InputField
                            id="notify-condition-value"
                            type={draftCondition.kind === 'rowCount' || draftCondition.kind === 'timeSince' ? 'number' : 'text'}
                            placeholder={
                                draftCondition.kind === 'rowCount'
                                    ? 'e.g. 5'
                                    : draftCondition.kind === 'timeSince'
                                        ? 'Minutes since event e.g. 30'
                                        : draftCondition.kind === 'columnContains'
                                            ? 'Text fragment e.g. error'
                                            : 'Expected value'
                            }
                            value={draftCondition.value}
                            onChange={(event) => {
                                const { value } = event.target;
                                setDraftCondition((previous) => ({
                                    ...previous,
                                    value,
                                }));
                            }}
                        />
                    </FormRow>

                    <FormRow>
                        <CheckboxRow htmlFor="notify-condition-slack">
                            <input
                                id="notify-condition-slack"
                                type="checkbox"
                                    checked={draftCondition.slackNotification === true}
                                onChange={(event) => {
                                    const { checked } = event.target;

                                    if (lockedNotificationType === 'MARK' && checked === true) {
                                        setDraftError('This set is locked to mark-only notifications. Remove existing conditions to enable Slack.');
                                        return;
                                    }

                                    if (lockedNotificationType === 'SLACK' && checked === false) {
                                        setDraftError('This set is locked to Slack notifications. Remove existing conditions to switch to mark-only.');
                                        return;
                                    }

                                    setDraftError('');
                                    setDraftCondition((previous) => ({
                                        ...previous,
                                        slackNotification: checked,
                                        notificationChannel:
                                            checked
                                                ? lockedNotificationChannel ||
                                                    previous.notificationChannel ||
                                                    DEFAULT_SLACK_NOTIFICATION_CHANNEL
                                                : previous.notificationChannel,
                                        ...(!checked ? { createHubspotTicket: false, ticket: { heading: '', pipeline: null, stage: null, label: null } } : {}),
                                    }));
                                }}
                            />
                            Send Slack notification when this set matches
                        </CheckboxRow>
                        <ModalText style={{ fontSize: theme.fontSize.superTiny }}>
                            Uncheck if this rule should only mark tables/rows without triggering Slack.
                        </ModalText>
                        {lockedNotificationType === 'MARK' && draftConditions.length > 0 && (
                            <ModalText style={{ fontSize: theme.fontSize.superTiny }}>
                                This condition set is mark-only. Remove existing conditions to enable Slack again.
                            </ModalText>
                        )}
                    </FormRow>

                    <FormRow>
                        <CheckboxRow htmlFor="notify-condition-hubspot">
                            <input
                                id="notify-condition-hubspot"
                                type="checkbox"
                                style={{ accentColor: '#FF7A59' }}
                                checked={draftCondition.createHubspotTicket === true}
                                disabled={draftCondition.slackNotification !== true}
                                onChange={(event) => {
                                    const { checked } = event.target;

                                    if (lockedHubspotTicket !== null && checked !== lockedHubspotTicket) {
                                        setDraftError('All conditions in this set must have the same HubSpot ticket setting. Remove existing conditions to change.');
                                        return;
                                    }

                                    setDraftError('');
                                    setDraftCondition((previous) => ({
                                        ...previous,
                                        createHubspotTicket: checked,
                                        ...(!checked ? { ticket: { heading: '', pipeline: null, stage: null, label: null } } : {}),
                                    }));
                                }}
                            />
                            Create HubSpot ticket when this set matches
                        </CheckboxRow>
                        <ModalText style={{ fontSize: theme.fontSize.superTiny }}>
                            Requires Slack notifications to be enabled.
                        </ModalText>
                        {lockedHubspotTicket !== null && draftConditions.length > 0 && (
                            <ModalText style={{ fontSize: theme.fontSize.superTiny }}>
                                HubSpot ticket setting is locked to match other conditions in this set.
                            </ModalText>
                        )}
                    </FormRow>

                    {draftCondition.createHubspotTicket === true && (
                        <FormRow>
                            <label htmlFor="notify-condition-heading">Ticket heading</label>
                            <InputField
                                id="notify-condition-heading"
                                style={{ borderColor: '#FF7A59' }}
                                placeholder="e.g. Hub offline for 30+ minutes"
                                value={draftCondition.ticket?.heading || ''}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    setDraftCondition((previous) => ({
                                        ...previous,
                                        ticket: { ...previous.ticket, heading: value },
                                    }));
                                }}
                            />
                        </FormRow>
                    )}

                    {draftCondition.createHubspotTicket === true && ticketPipelines.length > 0 && (
                        <FormRow>
                            <label htmlFor="notify-condition-pipeline">Ticket pipeline</label>
                            <Select
                                id="notify-condition-pipeline"
                                style={{ borderColor: '#FF7A59' }}
                                value={draftCondition.ticket?.pipeline?.idValue || ''}
                                onChange={(event) => {
                                    const selectedId = event.target.value;
                                    if (!selectedId) {
                                        setDraftCondition((previous) => ({
                                            ...previous,
                                            ticket: { ...previous.ticket, pipeline: null },
                                        }));
                                        return;
                                    }
                                    const match = ticketPipelines.find((p) => p.id === selectedId);
                                    setDraftCondition((previous) => ({
                                        ...previous,
                                        ticket: {
                                            ...previous.ticket,
                                            pipeline: match
                                                ? { label: match.label, idValue: match.id }
                                                : null,
                                        },
                                    }));
                                }}
                            >
                                <option value="">– None –</option>
                                {ticketPipelines.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                ))}
                            </Select>
                        </FormRow>
                    )}

                    {draftCondition.createHubspotTicket === true && ticketStages.length > 0 && (
                        <FormRow>
                            <label htmlFor="notify-condition-stage">Ticket stage</label>
                            <Select
                                id="notify-condition-stage"
                                style={{ borderColor: '#FF7A59' }}
                                value={draftCondition.ticket?.stage?.idValue || ''}
                                onChange={(event) => {
                                    const selectedId = event.target.value;
                                    if (!selectedId) {
                                        setDraftCondition((previous) => ({
                                            ...previous,
                                            ticket: { ...previous.ticket, stage: null },
                                        }));
                                        return;
                                    }
                                    const match = ticketStages.find((s) => s.id === selectedId);
                                    setDraftCondition((previous) => ({
                                        ...previous,
                                        ticket: {
                                            ...previous.ticket,
                                            stage: match
                                                ? { label: match.label, idValue: match.id }
                                                : null,
                                        },
                                    }));
                                }}
                            >
                                <option value="">– None –</option>
                                {ticketStages.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.label}
                                    </option>
                                ))}
                            </Select>
                        </FormRow>
                    )}

                    {draftCondition.createHubspotTicket === true && ticketLabels.length > 0 && (
                        <FormRow>
                            <label htmlFor="notify-condition-label">Ticket label</label>
                            <Select
                                id="notify-condition-label"
                                style={{ borderColor: '#FF7A59' }}
                                value={draftCondition.ticket?.label?.idValue || ''}
                                onChange={(event) => {
                                    const selectedValue = event.target.value;
                                    if (!selectedValue) {
                                        setDraftCondition((previous) => ({
                                            ...previous,
                                            ticket: { ...previous.ticket, label: null },
                                        }));
                                        return;
                                    }
                                    const match = ticketLabels.find((l) => l.value === selectedValue);
                                    setDraftCondition((previous) => ({
                                        ...previous,
                                        ticket: {
                                            ...previous.ticket,
                                            label: match
                                                ? { label: match.label, idValue: match.value }
                                                : null,
                                        },
                                    }));
                                }}
                            >
                                <option value="">– None –</option>
                                {ticketLabels.map((l) => (
                                    <option key={l.value} value={l.value}>
                                        {l.label}
                                    </option>
                                ))}
                            </Select>
                        </FormRow>
                    )}

                    <FormRow>
                        <label htmlFor="notify-condition-channel">Notification channel</label>
                        <Select
                            id="notify-condition-channel"
                            value={draftCondition.notificationChannel || DEFAULT_SLACK_NOTIFICATION_CHANNEL}
                            disabled={draftCondition.slackNotification !== true || lockedNotificationType === 'MARK'}
                            onChange={(event) => {
                                const { value } = event.target;
                                setDraftCondition((previous) => ({
                                    ...previous,
                                    notificationChannel: value,
                                }));
                            }}
                        >
                            {allowedNotificationChannels.map((channel) => (
                                <option key={channel.value} value={channel.value}>
                                    {channel.label}
                                </option>
                            ))}
                        </Select>
                        <ModalText style={{ fontSize: theme.fontSize.superTiny }}>
                            Default channel is Waved Error. Choose a different one for lower-urgency alerts.
                        </ModalText>
                        {lockedNotificationType === 'SLACK' && lockedNotificationChannel && (
                            <ModalText style={{ fontSize: theme.fontSize.superTiny }}>
                                Channel locked to {getNotificationChannelLabel(lockedNotificationChannel)} because other Slack notifications in this set already use it. Remove or disable those notifications to switch channels.
                            </ModalText>
                        )}
                    </FormRow>

                    {draftError && <ErrorMessage>{draftError}</ErrorMessage>}

                    <ButtonRowContainer style={{ paddingTop: '8px' }}>
                        <Button
                            primary
                            type="button"
                            disabled={!isDraftValid}
                            onClick={handleAddDraftCondition}
                        >
                            Add condition to set
                        </Button>

                        <Button tertiary type="button" onClick={handleCloseModal}>
                            Done
                        </Button>
                    </ButtonRowContainer>
                </ModalContainer>
            </Modal>
        </Section>
    );
};

export default CustomSqlNotificationEditor;
